import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessagesSquare, Loader2, RefreshCw, Mail, Bell, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminListFeedback, adminDeleteFeedback, adminNotifyUser } from '../lib/api';
import { useConfirmDialog } from '../components/ConfirmDialog';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// Canned notification the author receives when the admin clicks Notify.
// Kept short + reassuring so senders know their feedback was read.
const FEEDBACK_NOTED_MESSAGE = 'Thank you — your feedback has been noted by the Pray Now Together admin team.';

/**
 * Admin Feedback listing (`/admin/feedback`). Reads from the `feedback`
 * collection populated by the sidebar Feedback form. Admin-only.
 *
 * Each row now supports two actions:
 *   • Notify — sends the author a canned "your feedback has been noted"
 *     direct notification. Disabled for anonymous submissions.
 *   • Delete — permanently removes the entry from the `feedback`
 *     collection after a confirm prompt.
 */
const AdminFeedback = () => {
  const { user, loading: authLoading } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Per-row in-flight state so double-clicks don't fire twice and other
  // rows stay interactive while one row is busy.
  const [busyId, setBusyId] = useState(null);
  // Non-blocking toast at the top of the list. `{ tone, text }`.
  const [banner, setBanner] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await adminListFeedback();
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const notifyAuthor = async (row) => {
    if (!row.user_id) return;
    setBanner(null);
    setBusyId(row.id);
    try {
      await adminNotifyUser(row.user_id, FEEDBACK_NOTED_MESSAGE);
      setBanner({ tone: 'ok', text: `Notification sent to @${row.user_handle || row.user_id}.` });
    } catch (e) {
      setBanner({ tone: 'err', text: e?.response?.data?.detail || e.message || 'Notify failed' });
    } finally {
      setBusyId(null);
    }
  };

  const deleteRow = async (row) => {
    const ok = await confirm({
      title: 'Delete this feedback?',
      description: 'This will permanently remove the feedback entry from the queue. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    setBanner(null);
    setBusyId(row.id);
    try {
      await adminDeleteFeedback(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setBanner({ tone: 'ok', text: 'Feedback entry deleted.' });
    } catch (e) {
      setBanner({ tone: 'err', text: e?.response?.data?.detail || e.message || 'Delete failed' });
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <MessagesSquare size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-feedback-title">Admin · Feedback</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="refresh-feedback-btn"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {err && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="admin-feedback-error">{err}</div>}

      {banner && (
        <div
          data-testid="admin-feedback-banner"
          className={`p-3 rounded-lg text-sm mb-4 ${
            banner.tone === 'ok'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}
        >
          {banner.text}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading feedback…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-zinc-500" data-testid="admin-feedback-empty">No feedback yet.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-feedback-list">
          {rows.map((r) => {
            const rowBusy = busyId === r.id;
            const canNotify = !!r.user_id;
            return (
              <div
                key={r.id}
                data-testid={`feedback-row-${r.id}`}
                className="p-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/40"
              >
                <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
                  <div className="text-sm font-medium">
                    {r.user_handle ? (
                      <span className="text-amber-600 dark:text-amber-400">@{r.user_handle}</span>
                    ) : (
                      <span className="italic text-zinc-500">Anonymous</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 tabular-nums">{fmtDateTime(r.created_at)}</div>
                </div>
                <div className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">{r.message}</div>
                {r.email && (
                  <div className="mt-2 text-xs text-zinc-500 inline-flex items-center gap-1">
                    <Mail size={12} /> <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => notifyAuthor(r)}
                    disabled={!canNotify || rowBusy}
                    data-testid={`feedback-notify-btn-${r.id}`}
                    title={canNotify ? 'Send a "feedback noted" notification to the author' : 'Anonymous feedback — no author to notify'}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                    Notify
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRow(r)}
                    disabled={rowBusy}
                    data-testid={`feedback-delete-btn-${r.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
        <Link to="/admin/misuse-reports" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Misuse Reports</Link>
        <Link to="/admin/reports" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Prayer Reports</Link>
        <Link to="/admin/users" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Users</Link>
        <Link to="/admin/activity" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Activity</Link>
      </div>
      {confirmDialog}
    </div>
  );
};

export default AdminFeedback;
